import React, { useState } from 'react';
import { isInAppBrowser } from 'utils';
import InAppBrowserWarningModal from '../shared/InAppBrowserWarningModal';
import EventTypeSelector from './EventTypeSelector';
import AvailabilityViewer from '../Availability/AvailabilityViewer';

/**
 * BookingFlow - Manages the two-step booking process
 * Step 1: Select EventType (course)
 * Step 2: Select Time Slot (availability)
 *
 * @param {number} teacherId - User ID of the teacher
 * @param {string} teacherUuid - Teacher's webhook UUID (for API)
 * @param {string} referrerTeacherSlug - Referrer teacher slug (for tracking)
 * @param {string} referrerStudentUuid - Referrer student UUID (for tracking)
 * @param {object} translations - Translation object for i18n
 * @param {array} timezoneOptions - Timezone options from backend
 * @param {function} onComplete - Callback when user completes booking flow (optional)
 */
const BookingFlow = ({
  teacherId,
  teacherUuid,
  referrerTeacherSlug,
  referrerStudentUuid,
  translations = {},
  timezoneOptions = [],
  onComplete
}) => {
  // State management
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [step, setStep] = useState(1); // 1: EventType selection, 2: Time slot selection, 3: Booking confirmation
  const [selectedSlot, setSelectedSlot] = useState(null); // Store selected time slot for confirmation
  const [isSubmitting, setIsSubmitting] = useState(false); // Track booking submission state
  const [selectedTimezone, setSelectedTimezone] = useState(null); // Store selected timezone
  const [showInAppWarning, setShowInAppWarning] = useState(false);

  /**
   * Handle event type selection
   * Moves user to step 2 (time slot selection)
   */
  const handleEventTypeSelect = (eventType) => {
    setSelectedEventType(eventType);
    setStep(2);

    // Scroll to top for better UX on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handle back button click
   * Returns user to step 1 (event type selection)
   */
  const handleBack = () => {
    setSelectedEventType(null);
    setStep(1);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Check authentication and get user data
   * @returns {Object|null} User object or null if not authenticated
   */
  const checkAuthAndGetUser = async () => {
    try {
      const response = await fetch('/api/auth/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success && data.authenticated && data.user) {
        return data.user;
      }
      return null;
    } catch (error) {
      console.error('Failed to check auth status:', error);
      return null;
    }
  };

  /**
   * Redirect to student OAuth login
   */
  const redirectToStudentLogin = () => {
    if (isInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!csrfToken) {
      console.error('CSRF token not found');
      return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/users/auth/google_oauth2?role=student';

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  };

  /**
   * Track booking completion in Google Analytics
   * @param {number} bookingId - Created booking ID
   * @param {number} price - Booking price
   * @param {Object} metadata - Additional tracking data
   */
  const trackCompleteBooking = (bookingId, price, metadata = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'purchase', {
        transaction_id: bookingId,
        value: parseFloat(price) || 0,
        currency: 'TWD',
        items: [{
          item_id: metadata.event_type || 'unknown',
          item_name: metadata.event_type || 'Lesson',
          price: parseFloat(price) || 0,
          quantity: 1
        }]
      });
    }
  };

  /**
   * Convert slot data to ISO8601 datetime strings
   * @param {string} date - Date string (YYYY-MM-DD)
   * @param {Object|string} slot - Slot object with start/end or time string
   * @param {number} duration - Duration in minutes
   * @param {string} timezone - Timezone string
   * @returns {Object} { startTime, endTime } in ISO8601 format
   */
  const formatSlotTime = (date, slot, duration, timezone) => {
    // If slot has start_time and end_time (from API), use them directly
    if (slot.start_time && slot.end_time) {
      return {
        startTime: slot.start_time,
        endTime: slot.end_time
      };
    }

    // Otherwise, construct from date + slot.start
    const startTimeStr = typeof slot === 'string' ? slot : slot.start;
    const startTime = `${date}T${startTimeStr}:00`;

    // Calculate end time
    const start = new Date(startTime);
    const end = new Date(start.getTime() + duration * 60000);
    const endTime = end.toISOString().slice(0, 19);

    return {
      startTime: startTime,
      endTime: endTime
    };
  };

  /**
   * Handle time slot selection
   * Checks authentication and moves to confirmation step (step 3)
   */
  const handleSlotSelect = async (slotData) => {
    console.log('Selected slot:', slotData);

    // 1. Check authentication
    const user = await checkAuthAndGetUser();

    if (!user) {
      console.log('User not authenticated, redirecting to login...');
      redirectToStudentLogin();
      return;
    }

    // 2. Store selected slot and move to confirmation step
    setSelectedSlot(slotData);
    setStep(3);

    // Scroll to top for better UX on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handle booking confirmation
   * Creates booking via API after user confirms
   */
  const handleConfirmBooking = async () => {
    if (!selectedSlot || !selectedEventType) {
      console.error('Missing slot or event type data');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Get current user
      const user = await checkAuthAndGetUser();

      if (!user) {
        console.log('User not authenticated, redirecting to login...');
        redirectToStudentLogin();
        return;
      }

      // 2. Format time data
      const { startTime, endTime } = formatSlotTime(
        selectedSlot.date,
        selectedSlot.slot,
        selectedEventType.duration_minutes,
        selectedSlot.timezone
      );

      // 3. Prepare API request
      const requestBody = {
        booking: {
          teacher_uuid: teacherUuid,
          event_type_id: selectedEventType.id,
          event_type_name: selectedEventType.title,
          start_time: startTime,
          end_time: endTime,
          student_email: user.email,
          student_name: user.name,
          duration: selectedEventType.duration_minutes,
          student_timezone: selectedTimezone || 'Asia/Taipei',
          metadata: {
            referrer_teacher_slug: referrerTeacherSlug,
            referrer_student_uuid: referrerStudentUuid
          }
        }
      };

      console.log('Creating booking with:', requestBody);

      // 4. Create booking via API
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        // 5. Track analytics
        trackCompleteBooking(
          data.booking.id,
          selectedEventType.price || 0,
          {
            teacher_id: teacherId,
            event_type: selectedEventType.title
          }
        );

        // 6. Redirect to payment verification page
        console.log('Booking created successfully, redirecting to:', data.redirect_url);
        window.location.href = data.redirect_url;
      } else {
        console.error('Booking creation failed:', data);
        alert(`Booking failed: ${data.message || 'Unknown error'}\n\n${data.errors ? data.errors.join('\n') : ''}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('API call failed:', error);
      alert('Booking failed, please try again later');
      setIsSubmitting(false);
    }
  };

  /**
   * Format date for display
   * Returns date in locale-specific format (e.g., "2025年11月23日" for zh-TW)
   */
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // Get current locale from HTML lang attribute
    const locale = document.documentElement.lang || 'zh-TW';

    if (locale.startsWith('zh')) {
      // Chinese format: YYYY年MM月DD日
      return `${year}年${month}月${day}日`;
    } else if (locale === 'ja') {
      // Japanese format: YYYY年MM月DD日
      return `${year}年${month}月${day}日`;
    } else {
      // English and other locales: use standard date format
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  /**
   * Get day of week name
   * Returns localized day name (e.g., "星期日" for Sunday in zh-TW)
   */
  const getDayOfWeek = (date) => {
    const days = translations.step3?.days || {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday'
    };

    const dayNames = [
      days.sunday,
      days.monday,
      days.tuesday,
      days.wednesday,
      days.thursday,
      days.friday,
      days.saturday
    ];

    return dayNames[date.getDay()];
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-4">
      {/* Step 1: EventType Selection */}
      {step === 1 && (
        <EventTypeSelector
          teacherId={teacherId}
          onEventTypeSelect={handleEventTypeSelect}
          translations={translations}
        />
      )}

      {/* Step 2: Time Slot Selection */}
      {step === 2 && selectedEventType && (
        <div>
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="mb-4 inline-flex items-center gap-2 text-gray-600 hover:text-[#3366FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 rounded-lg px-3 py-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">
              {translations.back_to_courses || 'Back to Course Selection'}
            </span>
          </button>

          {/* Selected EventType Info Card */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedEventType.title}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedEventType.duration_minutes} {translations.minutes || 'minutes'}
                  {selectedEventType.price && parseFloat(selectedEventType.price) > 0 && (
                    <span> • {selectedEventType.currency === 'TWD' ? 'NT$' : selectedEventType.currency} {parseFloat(selectedEventType.price).toLocaleString()}</span>
                  )}
                </p>
              </div>
              <button
                onClick={handleBack}
                className="text-sm text-[#3366FF] hover:underline focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 rounded px-2 py-1"
              >
                {translations.change || 'Change'}
              </button>
            </div>
          </div>

          {/* AvailabilityViewer Component with built-in timezone selector */}
          <AvailabilityViewer
            eventTypeId={selectedEventType.id}
            timezone={selectedTimezone || 'Asia/Taipei'}
            timezoneOptions={timezoneOptions}
            onSlotSelect={handleSlotSelect}
            onTimezoneChange={setSelectedTimezone}
            translations={translations}
          />
        </div>
      )}

      {/* Step 3: Booking Confirmation */}
      {step === 3 && selectedSlot && selectedEventType && (
        <div>
          {/* Back Button */}
          <button
            onClick={() => setStep(2)}
            className="mb-4 inline-flex items-center gap-2 text-gray-600 hover:text-[#3366FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 rounded-lg px-3 py-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">
              {translations.step3?.back_to_time_selection || 'Back to Time Selection'}
            </span>
          </button>

          {/* Confirmation Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {translations.step3?.confirm_title || 'Confirm Booking Details'}
            </h2>

            {/* Booking Details */}
            <div className="space-y-4">
              {/* Date */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">{translations.step3?.date_label || 'Booking Date'}</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(selectedSlot.date)} ({getDayOfWeek(selectedSlot.date)})
                  </p>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">{translations.step3?.time_label || 'Booking Time'}</p>
                  <p className="font-medium text-gray-900">
                    {selectedSlot.slot.start} - {selectedSlot.slot.end}
                  </p>
                </div>
              </div>

              {/* Course */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">{translations.step3?.course_label || 'Course'}</p>
                  <p className="font-medium text-gray-900">{selectedEventType.title}</p>
                  <p className="text-sm text-gray-600">{selectedEventType.duration_minutes} {translations.minutes || 'minutes'}</p>
                </div>
              </div>

              {/* Price */}
              {selectedEventType.price && parseFloat(selectedEventType.price) > 0 && (
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-500">{translations.step3?.price_label || 'Price'}</p>
                    <p className="font-medium text-gray-900">
                      {selectedEventType.currency === 'TWD' ? 'NT$' : selectedEventType.currency} {parseFloat(selectedEventType.price).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Timezone */}
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-gray-500">{translations.step3?.timezone_label || 'Timezone'}</p>
                  <p className="font-medium text-gray-900">{selectedTimezone || 'Asia/Taipei'}</p>
                </div>
              </div>
            </div>

            {/* Confirm Button */}
            <button
              onClick={handleConfirmBooking}
              disabled={isSubmitting}
              className="mt-6 w-full bg-[#3366FF] hover:bg-[#2952CC] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{translations.step3?.submitting || 'Booking...'}</span>
                </>
              ) : (
                <span>{translations.step3?.confirm_button || 'Confirm Booking'}</span>
              )}
            </button>
          </div>
        </div>
      )}
      <InAppBrowserWarningModal
        isOpen={showInAppWarning}
        onClose={() => setShowInAppWarning(false)}
        translations={translations?.in_app_browser}
      />
    </div>
  );
};

export default BookingFlow;
